import React, { createContext, forwardRef, useContext, useMemo } from 'react'
import { twMerge } from 'tailwind-merge'

import {
    Button as GsButton,
    ButtonGroup,
    ButtonIcon as GsButtonIcon,
    ButtonSpinner as GsButtonSpinner,
    ButtonText as GsButtonText,
} from '../../_gluestack/button'
import { FOCUS_RING_CLASS } from '../../focusRing'
import { Icon } from '../icon'
import { type IconProps } from '../icon/Icon.types'
import {
    buttonHeight,
    type ButtonSize,
    type ButtonTone,
    type ButtonVariant,
    resolveButtonContainer,
    resolveButtonLabel,
    resolveButtonSpinnerColor,
} from './variants'

// Thin adapters over the vendored Gluestack compound Button. `variant`
// (primary | secondary | text) and `tone` (default | destructive) are
// prop-driven — the parent resolves its own container classes and publishes the
// pair on a context so `ButtonText` / `ButtonIcon` pick the matching label /
// icon colour automatically. Per-call overrides still win: any `className` (or
// explicit ButtonIcon `color`) is twMerge-d last. The vendored Gluestack
// compound API (children, size, a11y props) is preserved untouched.

type ButtonStyleContextValue = { variant: ButtonVariant; tone: ButtonTone }
const ButtonStyleContext = createContext<ButtonStyleContextValue>({
    variant: 'primary',
    tone: 'default',
})

type GsButtonRef = React.ComponentRef<typeof GsButton>
// Gluestack's compound Button ships its own `variant`/`action` props; drop them
// so our prop-driven `variant` + `tone` own the type (and aren't forwarded to
// the vendored component, which we style entirely via className).
export type ButtonProps = Omit<
    React.ComponentPropsWithoutRef<typeof GsButton>,
    'variant' | 'action' | 'size'
> & {
    className?: string
    variant?: ButtonVariant
    tone?: ButtonTone
    size?: ButtonSize
}

// Figma "Button" base: 12px x-padding (px-3), 8px y-padding (py-2), 4px gap
// (gap-1), 4px radius. The generated DS contract (`aos-ds/.../button.contract`)
// carries the height on the `size` axis — `big` is 48px, `small` is 40px — so
// it is a prop here rather than something the padding has to add up to.
//
// The shared `focus-visible:` ring is merged in here because the vendored Gluestack
// base's own `data-[focus-visible=true]:web:ring-2` never fires: nothing in this
// package sets that data attribute on web.
const buttonBase = `rounded flex-row items-center justify-center gap-1 py-2 px-3 border-2 border-transparent ${FOCUS_RING_CLASS}`

// react-native-web renders the button as a <div>, so the `disabled:` pseudo
// never matches and `:hover`/`:active` keep firing. Strip the interaction
// classes in JS and mute instead — `disabled` on the Pressable blocks presses.
const stripInteractionClasses = (cls: string): string =>
    cls
        .split(' ')
        .filter(c => !c.startsWith('hover:') && !c.startsWith('active:'))
        .join(' ')

export const Button = forwardRef<GsButtonRef, ButtonProps>(
    (
        {
            className,
            children,
            variant = 'primary',
            tone = 'default',
            size = 'small',
            disabled,
            isDisabled,
            ...rest
        },
        ref,
    ) => {
        const styleContext = useMemo<ButtonStyleContextValue>(
            () => ({ variant, tone }),
            [variant, tone],
        )
        const container = resolveButtonContainer(variant, tone)
        // The vendored Gluestack root spreads props AFTER its own
        // `disabled={isDisabled}`, so a stray `disabled: undefined` from this
        // wrapper would re-enable presses on a visually disabled button —
        // resolve both prop spellings here and forward a single truth.
        const resolvedDisabled = disabled ?? isDisabled ?? false
        return (
            <ButtonStyleContext.Provider value={styleContext}>
                <GsButton
                    ref={ref}
                    disabled={resolvedDisabled}
                    isDisabled={resolvedDisabled}
                    className={twMerge(
                        buttonBase,
                        buttonHeight[size],
                        resolvedDisabled
                            ? `${stripInteractionClasses(container)} opacity-40 web:pointer-events-none web:cursor-not-allowed`
                            : container,
                        className,
                    )}
                    {...rest}
                >
                    {children}
                </GsButton>
            </ButtonStyleContext.Provider>
        )
    },
)
Button.displayName = 'Button'

type GsButtonTextRef = React.ComponentRef<typeof GsButtonText>
export type ButtonTextProps = React.ComponentPropsWithoutRef<typeof GsButtonText> & {
    className?: string
}

const buttonTextBase = 'px-1 font-semiBold text-[0.875rem] leading-[1.25rem]'

export const ButtonText = forwardRef<GsButtonTextRef, ButtonTextProps>(
    ({ className, children, ...rest }, ref) => {
        const { variant, tone } = useContext(ButtonStyleContext)
        return (
            <GsButtonText
                ref={ref}
                className={twMerge(buttonTextBase, resolveButtonLabel(variant, tone), className)}
                {...rest}
            >
                {children}
            </GsButtonText>
        )
    },
)
ButtonText.displayName = 'ButtonText'

type IconRef = React.ComponentRef<typeof Icon>
export type ButtonIconProps = Omit<
    React.ComponentPropsWithoutRef<typeof GsButtonIcon>,
    'as' | 'size' | 'width' | 'height' | 'color' | 'fill'
> &
    Pick<IconProps, 'source' | 'as' | 'size' | 'color'> & {
        className?: string
    }

// Button-icon pixel scale (kept on the Button so icon sizes don't shift when
// the standalone `Icon` follows Gluestack's own size scale).
const BUTTON_ICON_PX: Record<string, number> = {
    '2xs': 12,
    'xs': 16,
    'sm': 20,
    'md': 24,
    'lg': 32,
    'xl': 40,
}
const resolveButtonIconPx = (size: IconProps['size']): number =>
    typeof size === 'number' ? size : BUTTON_ICON_PX[size ?? 'md']

// Renders through the standalone `Icon` (not Gluestack's `ButtonIcon`): only that
// wrapper forwards a className colour to a fill-based glyph, which is what lets
// the variant ink follow the hover/pressed fill. An explicit `color` still wins.
export const ButtonIcon = forwardRef<IconRef, ButtonIconProps>(
    ({ source, as: asProp, size, color, className, ...rest }, ref) => {
        const { variant, tone } = useContext(ButtonStyleContext)
        const SvgComponent = asProp ?? source
        if (!SvgComponent) {
            return null
        }
        return (
            <Icon
                ref={ref}
                as={SvgComponent}
                size={resolveButtonIconPx(size)}
                color={color}
                className={twMerge(resolveButtonLabel(variant, tone), className)}
                {...rest}
            />
        )
    },
)
ButtonIcon.displayName = 'ButtonIcon'

type GsButtonSpinnerRef = React.ComponentRef<typeof GsButtonSpinner>
export type ButtonSpinnerProps = React.ComponentPropsWithoutRef<typeof GsButtonSpinner>

// The busy indicator is button content, so it carries the same ink as the label
// — a light spinner on a filled button, not the ActivityIndicator's own grey.
export const ButtonSpinner = forwardRef<GsButtonSpinnerRef, ButtonSpinnerProps>(
    ({ color, ...rest }, ref) => {
        const { variant, tone } = useContext(ButtonStyleContext)
        return (
            <GsButtonSpinner
                ref={ref}
                color={color ?? resolveButtonSpinnerColor(variant, tone)}
                {...rest}
            />
        )
    },
)
ButtonSpinner.displayName = 'ButtonSpinner'

export { ButtonGroup }
